import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Wordmark } from "../components/BrandLogo";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.info(t("reset.too_short"));
    if (pw !== pw2) return toast.error(t("reset.mismatch"));
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("reset.link_invalid"));
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <div className="ss-soft-bg min-h-[100dvh] w-full">
        <div className="mx-auto w-full max-w-md min-h-[100dvh] px-8 py-10 flex flex-col">
          <Wordmark className="mb-10" />
          <h1 className="font-serif text-3xl">{t("reset.no_token_h")}</h1>
          <p className="mt-3 text-foreground/65">{t("reset.no_token_sub")}</p>
          <Link to="/forgot" className="mt-6 text-brand text-sm">{t("reset.send_new")}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="ss-soft-bg min-h-[100dvh] w-full">
      <div className="mx-auto w-full max-w-md min-h-[100dvh] px-8 py-10 flex flex-col">
        <Wordmark className="mb-10" />
        {!done ? (
          <>
            <h1 className="font-serif text-3xl">{t("reset.h")}</h1>
            <p className="text-foreground/65 text-sm mt-2">{t("reset.sub")}</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-foreground/60">{t("reset.new_password")}</Label>
                <Input
                  data-testid="reset-pw-input"
                  type="password"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="mt-1 h-13 rounded-2xl bg-card border-border px-5"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-foreground/60">{t("reset.confirm")}</Label>
                <Input
                  data-testid="reset-pw2-input"
                  type="password"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  className="mt-1 h-13 rounded-2xl bg-card border-border px-5"
                />
              </div>
              <Button
                data-testid="reset-submit-btn"
                disabled={busy}
                className="w-full h-14 rounded-full bg-brand hover:bg-brand/90 text-white text-base"
              >
                {busy ? t("common.saving") : t("reset.save_new")}
              </Button>
            </form>
          </>
        ) : (
          <div data-testid="reset-done" className="animate-fade-up">
            <p className="font-script text-2xl text-brand">{t("reset.saved_kicker")}</p>
            <h1 className="font-serif text-3xl mt-1 leading-tight">{t("reset.saved_h")}</h1>
            <p className="mt-3 text-foreground/70 leading-relaxed">{t("reset.saved_sub")}</p>
            <Button
              data-testid="reset-go-signin"
              onClick={() => navigate("/auth")}
              className="mt-8 w-full h-13 rounded-full bg-brand hover:bg-brand/90 text-white"
            >
              {t("reset.go_signin")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
